import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { EXTENSION_MATCH_WINDOW_DAYS } from '../../planning/utils/rentalExtensionSuggestions';
import type { ExtensionSettings } from '../types';

const DEFAULT_SETTINGS: ExtensionSettings = {
  windowDays: EXTENSION_MATCH_WINDOW_DAYS,
  considerOptions: false,
};

export function useExtensionSettings(userId: string | null) {
  const [settings, setSettings] = useState<ExtensionSettings>(DEFAULT_SETTINGS);

  const loadSettings = async () => {
    if (!userId) return;
    try {
      const { data } = await supabase
        .from('extension_settings')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();
      if (data) {
        setSettings({ windowDays: data.window_days, considerOptions: data.consider_options });
      }
    } catch {
      // Non-critical: UI defaults are safe fallbacks when settings can't be loaded
    }
  };

  useEffect(() => {
    loadSettings();
  }, [userId]);

  const updateSettings = async (next: ExtensionSettings): Promise<boolean> => {
    if (!userId) return false;
    try {
      const { error } = await supabase.from('extension_settings').upsert(
        { user_id: userId, window_days: next.windowDays, consider_options: next.considerOptions },
        { onConflict: 'user_id' }
      );
      if (error) throw error;
      setSettings(next);
      return true;
    } catch {
      return false;
    }
  };

  return { settings, updateSettings };
}
